import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { ArrowLeft, Eye, FileDown, FolderDown, Activity } from "lucide-react";
import { t } from "../../lib/i18n";

const TYPES = {
  im_view: { label: t("Abrió el IM"), icon: Eye, color: "text-[#2E6F80]" },
  im_download: { label: t("Descargó el IM en PDF"), icon: FileDown, color: "text-[#1E3A5F]" },
  doc_download: { label: t("Descargó del Data Room"), icon: FolderDown, color: "text-[#E0712C]" },
};
const FILTERS = [["all", "Todo"], ["im_view", "Aperturas IM"], ["im_download", "Descargas IM"], ["doc_download", "Documentos"]];
const fmt = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const roleLabel = (r) => (r === "investor" ? "Inversor" : "Searcher");

export default function AccessLog() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get(`/companies/${id}`).then((r) => setCompany(r.data));
    api.get(`/companies/${id}/access-log`).then((r) => setData(r.data)).catch(() => setData({ events: [], buyers: [] }));
  }, [id]);

  if (!data) return <Layout><div className="px-10 py-20 font-mono-label text-muted-foreground animate-pulse">{t("Cargando registro…")}</div></Layout>;
  const events = data.events.filter((e) => filter === "all" || e.type === filter);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="access-log-page">
        <Link to={`/seller/company/${id}`} className="font-mono-label text-muted-foreground inline-flex items-center gap-2 hover:text-foreground mb-6" data-testid="access-log-back">
          <ArrowLeft className="w-3 h-3" />{" "}{t("Volver a la empresa")}
        </Link>
        <div className="mb-10">
          <span className="font-mono-label text-gold flex items-center gap-2"><Activity className="w-3 h-3" />{" "}{t("Registro de accesos")}</span>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">{company?.name || t("Empresa")}</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            {t("Quién ha abierto tu Information Memorandum, quién lo ha descargado y qué documentos del Data Room se ha llevado cada comprador. Una señal directa de su nivel de interés.")}
          </p>
        </div>

        <section className="mb-10" data-testid="access-log-buyers">
          <span className="font-mono-label text-[#E0712C]">{t("Interés por comprador")}</span>
          {data.buyers.length === 0 ? (
            <div className="hairline bg-card p-10 text-center mt-3" data-testid="access-log-empty">
              <p className="font-serif-display text-xl mb-1">{t("Todavía nadie ha abierto tu IM")}</p>
              <p className="text-sm text-muted-foreground">{t("Cuando un comprador con NDA aprobado abra el IM o descargue documentación, aparecerá aquí.")}</p>
            </div>
          ) : (
            <div className="hairline bg-card mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="hairline-b font-mono-label text-muted-foreground text-left" style={{ fontSize: "0.6rem" }}>
                    <th className="px-5 py-3 font-normal">{t("Comprador")}</th>
                    <th className="px-5 py-3 font-normal text-right">{t("Aperturas IM")}</th>
                    <th className="px-5 py-3 font-normal text-right">{t("Descargas IM")}</th>
                    <th className="px-5 py-3 font-normal text-right">{t("Docs. Data Room")}</th>
                    <th className="px-5 py-3 font-normal text-right">{t("Último acceso")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.buyers.map((b) => (
                    <tr key={b.user_id} className="hairline-t" data-testid={`access-buyer-${b.user_id}`}>
                      <td className="px-5 py-4">
                        <div className="font-medium">{b.user_name}</div>
                        <div className="font-mono-label text-muted-foreground" style={{ fontSize: "0.58rem" }}>{roleLabel(b.role)}</div>
                      </td>
                      <td className="px-5 py-4 text-right font-serif-display text-xl">{b.im_views}</td>
                      <td className="px-5 py-4 text-right font-serif-display text-xl">{b.im_downloads}</td>
                      <td className="px-5 py-4 text-right font-serif-display text-xl">{b.doc_downloads}</td>
                      <td className="px-5 py-4 text-right text-muted-foreground whitespace-nowrap">{fmt(b.last_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section data-testid="access-log-timeline">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="font-mono-label text-[#E0712C]">{t("Actividad reciente")}</span>
            <div className="flex gap-0 hairline border-r-0">
              {FILTERS.map(([k, label]) => (
                <button key={k} type="button" onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 text-xs hairline border-l-0 border-y-0 transition-colors ${filter === k ? "bg-[#1E3A5F] text-bone" : "bg-card hover:bg-bone"}`}
                  data-testid={`access-filter-${k}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="hairline bg-card mt-3">
            {events.length === 0 ? (
              <p className="p-8 text-sm text-muted-foreground text-center">{t("Sin actividad en esta categoría.")}</p>
            ) : events.map((e, i) => {
              const t = TYPES[e.type];
              return (
                <div key={i} className={`px-5 py-3.5 flex items-center gap-4 ${i > 0 ? "hairline-t" : ""}`} data-testid={`access-event-${i}`}>
                  <t.icon className={`w-4 h-4 flex-shrink-0 ${t.color}`} />
                  <div className="flex-1 min-w-0 text-sm">
                    <span className="font-medium">{e.user_name}</span>
                    <span className="text-muted-foreground"> · {roleLabel(e.role)} · {t.label}</span>
                    {e.filename && <span className="text-muted-foreground"> · <span className="text-foreground">{e.filename}</span></span>}
                  </div>
                  <span className="font-mono-label text-muted-foreground whitespace-nowrap" style={{ fontSize: "0.6rem" }}>{fmt(e.created_at)}</span>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </Layout>
  );
}
